import React, {
  FC,
  KeyboardEvent,
  MouseEvent,
  TouchEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import closeIcon from '~Assets/icons/close.svg';
import Keyboard from '../Keyboard';
import { caretIndexAt, paintCaret } from './caret';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

/**
 * Big, uppercase, letter-spaced - a code is read back to a cashier or typed
 * from a flyer, so the glyphs need room. Uncontrolled: the value lives in the
 * DOM and is written by the on-screen keyboard, so typing never renders anything.
 */
const Field = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  padding: 0 28px;
  border-radius: 24px;
  border: 2px solid var(--eposBorderColor);
  outline: none;
  font-family: var(--eposFontFamily);
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  background: var(--eposSurface);
  /* The on-screen keyboard owns input, so the field never shows a native caret or selection. */
  caret-color: transparent;

  /* Free text (a comment) keeps its case and normal spacing. */
  &.is-free-text {
    letter-spacing: 0;
    text-transform: none;
    font-size: 24px;
    font-weight: 600;
  }
  color: var(--eposText);
  transition: border-color 0.15s ease-out;

  &::placeholder {
    letter-spacing: 0;
    text-transform: none;
    font-weight: 600;
    opacity: 0.4;
    color: var(--eposText);
  }

  &:focus {
    border-color: var(--eposButton);
  }

  /* The caret is drawn by Caret; a stray range selection is never shown either. */
  &::selection {
    background: transparent;
  }

  .has-error & {
    border-color: var(--colorUIRed);
    /* Room for the clear button so the text never runs under it. */
    padding-right: 88px;
  }
`;

/**
 * The cross at the field's right edge. Shown only with an error - pure CSS
 * off `has-error`, so it never renders anything of its own.
 */
const Clear = styled.button`
  appearance: none;
  position: absolute;
  top: 50%;
  right: 12px;
  width: 64px;
  height: 64px;
  margin-top: -32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease-out;

  .has-error & {
    display: flex;
  }

  &.is-touching {
    transform: scale(0.9);
  }
`;

/** Holds the field and the drawn caret on top of it. */
const FieldBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
`;

/**
 * The blinking caret. The field is `readOnly`, so the browser draws none.
 * Shown only while the field has focus - pure CSS, no state - and moved by
 * `paintCaret` through its transform. Two identical blinks so swapping
 * `is-blink-restart` restarts it, solid, after every key.
 */
const Caret = styled.span`
  position: absolute;
  top: 50%;
  left: 0;
  width: 3px;
  height: 36px;
  margin-top: -18px;
  border-radius: 2px;
  background: var(--eposButton);
  pointer-events: none;
  display: none;
  animation: eposCaretBlink 1s step-end infinite;

  input:focus + & {
    display: block;
  }

  .is-free-text + & {
    height: 30px;
    margin-top: -15px;
  }

  &.is-blink-restart {
    animation-name: eposCaretBlinkRestart;
  }

  @keyframes eposCaretBlink {
    50% {
      opacity: 0;
    }
  }

  @keyframes eposCaretBlinkRestart {
    50% {
      opacity: 0;
    }
  }
`;

const Submit = styled.button`
  appearance: none;
  flex: 0 0 auto;
  min-height: 88px;
  padding: 0 36px;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 24px;
  font-weight: 700;
  background: var(--eposButton);
  color: var(--eposButtonText);
  transition: transform 0.15s ease-out;

  &.is-touching {
    transform: scale(0.97);
  }
`;

/**
 * Sits right under the field and folds open from zero height when there is
 * an error, the text sliding down from above; folds shut again when it clears.
 * Same `grid-template-rows` trick as the keyboard, so nothing is measured.
 */
const ErrorFold = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 220ms cubic-bezier(0.22, 1, 0.36, 1);

  .has-error & {
    grid-template-rows: 1fr;
  }
`;

const Error = styled.div`
  min-height: 0;
  overflow: hidden;
  padding: 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--colorUIRed);
  transform: translate3d(0, -10px, 0);
  opacity: 0;
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out;

  .has-error & {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
`;

interface Props {
  placeholder: string;
  submitLabel: string;
  /** Called with the trimmed value - uppercased unless `freeText`. */
  onSubmit: (code: string) => void;
  error?: string;
  autoFocus?: boolean;
  /** Free text (a comment): keeps case, may be submitted empty to clear, capped at `maxLength`. */
  freeText?: boolean;
  maxLength?: number;
  /** What the field starts with - a comment being edited. */
  defaultValue?: string;
  /** After every key that changed the field, e.g. to clear `error` once the shopper types again. */
  onChange?: () => void;
}

/**
 * One-line code entry with a submit button and the EPOS keyboard folded out
 * underneath. The field is `readOnly` with `inputMode="none"`, so the device's
 * own keyboard never comes up; a hardware keyboard's Enter still submits. The
 * field is uncontrolled so keystrokes stay in the DOM - the component renders
 * only when `error` changes. The caret is the field's own selection, drawn by
 * `Caret` and moved by a tap on the text; neither typing nor tapping renders.
 */
const CodeInput: FC<Props> = ({
  placeholder,
  submitLabel,
  onSubmit,
  error,
  autoFocus = true,
  freeText = false,
  maxLength,
  defaultValue,
  onChange,
}) => {
  const fieldRef = useRef<HTMLInputElement>(null);
  const foldRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLSpanElement>(null);
  /** Read through a ref so the keyboard's callback stays stable whatever the parent passes. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /** Puts the drawn caret where the field's selection is. Stable - Keyboard calls it on every key. */
  const repaintCaret = useCallback(() => {
    const field = fieldRef.current;
    const caret = caretRef.current;
    if (field && caret) {
      paintCaret(field, caret);
    }
  }, []);

  /** A key changed the field: move the caret and tell the parent. */
  const handleKeyPress = useCallback(() => {
    repaintCaret();
    onChangeRef.current?.();
  }, [repaintCaret]);

  /** A tap on the text moves the caret to the nearest gap between letters. */
  const placeCaretAt = useCallback(
    (clientX: number) => {
      const field = fieldRef.current;
      if (!field) return;
      const index = caretIndexAt(field, clientX);
      field.focus();
      field.setSelectionRange(index, index);
      repaintCaret();
    },
    [repaintCaret],
  );

  /**
   * Both run on a touch device, and the click comes last - after the browser
   * has focused the field and placed its own selection - so it has the final say.
   */
  const handleFieldTouchEnd = useCallback(
    (event: TouchEvent<HTMLInputElement>) => {
      const touch = event.changedTouches[0];
      if (touch) placeCaretAt(touch.clientX);
    },
    [placeCaretAt],
  );
  const handleFieldClick = useCallback(
    (event: MouseEvent<HTMLInputElement>) => placeCaretAt(event.clientX),
    [placeCaretAt],
  );

  /** Fold the keyboard out on the frame after mount, so the transition actually plays. */
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => foldRef.current?.classList.add('is-open'));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      /** After the modal's entrance - focus shows the border and caret, the keyboard is ours. */
      const timer = window.setTimeout(() => {
        const field = fieldRef.current;
        if (!field) return;
        field.focus();
        /** A comment being edited carries on from its end. */
        field.setSelectionRange(field.value.length, field.value.length);
        repaintCaret();
      }, 250);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [autoFocus, repaintCaret]);

  const submit = useCallback(() => {
    const raw = fieldRef.current?.value.trim() ?? '';
    const value = freeText ? raw : raw.toUpperCase();
    if (value || freeText) {
      onSubmit(value);
    }
  }, [onSubmit, freeText]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const { touching, disabled, disabledToast, ...button } = useButton({ onClick: submit });

  /** Empties the field and puts the caret back - the parent's onChange then clears the error. */
  const clear = useCallback(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.value = '';
    field.focus();
    field.setSelectionRange(0, 0);
    repaintCaret();
    onChangeRef.current?.();
  }, [repaintCaret]);
  const {
    touching: clearTouching,
    disabled: cd,
    disabledToast: ct,
    ...clearButton
  } = useButton({ onClick: clear });

  return (
    <Wrapper className={error ? 'has-error' : undefined}>
      <Row>
        <FieldBox>
          <Field
            ref={fieldRef}
            type="text"
            readOnly
            inputMode="none"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className={freeText ? 'is-free-text' : undefined}
            maxLength={maxLength}
            defaultValue={defaultValue}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onTouchEnd={handleFieldTouchEnd}
            onClick={handleFieldClick}
            data-testid="epos-code-input"
          />
          <Caret ref={caretRef} data-testid="epos-code-caret" />
          <Clear
            {...clearButton}
            type="button"
            aria-label="Clear"
            className={clearTouching ? 'is-touching' : undefined}
            data-testid="epos-code-clear">
            <Icon src={closeIcon} color="var(--eposText)" width="32px" height="32px" />
          </Clear>
        </FieldBox>
        <Submit
          {...button}
          className={touching ? 'is-touching' : undefined}
          data-testid="epos-code-submit">
          {submitLabel}
        </Submit>
      </Row>
      <ErrorFold>
        <Error data-testid="epos-code-error">{error}</Error>
      </ErrorFold>
      <Keyboard
        inputRef={fieldRef}
        foldRef={foldRef}
        freeText={freeText}
        maxLength={maxLength}
        onPress={handleKeyPress}
        testId="epos-keyboard"
      />
    </Wrapper>
  );
};

export default memo(CodeInput);
